import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Building2,
  Calendar,
  FileText,
  ArrowRight,
  TrendingUp,
  CheckCircle2,
  Clock,
  MoreVertical,
  Loader2,
  LogOut
} from "lucide-react";
import { useEngagement } from "@/contexts/EngagementContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSharePointConfig } from "@/contexts/SharePointConfigContext";
import { apiService, Engagement as ApiEngagement, AuditClient } from "@/services/api-service";
import { Engagement } from "@/types/engagement";
import { EngagementCreationModal } from "@/components/engagements/EngagementCreationModal";
import logoSrc from "/logo.png";
import logoNameDark from "/logo_name_dark.png";

export default function EngagementHomePage() {
  const { engagements: localEngagements, refreshEngagements, setCurrentEngagement } = useEngagement();
  const { user, isAuthenticated, logout } = useAuth();
  const { loadConfig } = useSharePointConfig();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [apiEngagements, setApiEngagements] = useState<ApiEngagement[]>([]);
  const [clients, setClients] = useState<Map<string, AuditClient>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // All users can now create engagements - no role check needed

  // Load engagements from API
  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: "/login" });
      return;
    }

    loadEngagements();
  }, [isAuthenticated, navigate]);

  const loadEngagements = async () => {
    try {
      setIsLoading(true);
      setError("");
      
      // Get engagements from API - filter by main = true for dashboard
      const engagements = await apiService.getUserEngagements({ for_main: true });
      console.log("Loaded engagements (main = true):", engagements);
      // Log teamMembers structure for debugging
      if (engagements.length > 0 && engagements[0].teamMembers) {
        console.log("Sample teamMember structure:", engagements[0].teamMembers[0]);
      }
      setApiEngagements(engagements);

      // Build clients map from engagement data (client_name is included in response)
      const clientsMap = new Map<string, AuditClient>();
      engagements.forEach(engagement => {
        if (engagement.audit_client_id && engagement.client_name) {
          clientsMap.set(engagement.audit_client_id, {
            id: engagement.audit_client_id,
            firm_id: '', // Not needed for display
            client_name: engagement.client_name,
            status: 'Active' // Default, could come from API if needed
          });
        }
      });

      setClients(clientsMap);
    } catch (err: any) {
      console.error("Error loading engagements:", err);
      setError(err.message || "Failed to load engagements");
    } finally {
      setIsLoading(false);
    }
  };

  // Logo asset paths (Vite will handle hashing/bundling)
  const logoPath = logoSrc;

  const handleOpenEngagement = async (engagement: ApiEngagement) => {
    try {
      setIsLoading(true);
      setError("");

      // Get client name for config
      const client = clients.get(engagement.audit_client_id);
      const clientName = client?.client_name || engagement.client_name || 'Unknown Client';
      const engagementName = engagement.engagement_name || `Engagement ${engagement.id.substring(0, 8)}`;

      console.log('Loading engagement:', {
        engagementId: engagement.id,
        clientName,
        engagementName
      });

      // Load SharePoint config first
      await loadConfig(engagement.id, clientName, engagementName);

      console.log('SharePoint config loaded, setting engagement and navigating...');

      // Convert API engagement to local format if needed
      const localEngagement: Engagement = {
        id: engagement.id,
        clientId: engagement.audit_client_id,
        clientName: clientName,
        engagementName: engagementName,
        typeOfEngagement: 'Financial statement audit', // Default, should come from API
        financialReportingFramework: undefined,
        reportingDate: undefined,
        createdAt: engagement.created_at || new Date().toISOString(),
        updatedAt: engagement.updated_at || new Date().toISOString(),
      };

      // Set engagement in context
      setCurrentEngagement(localEngagement);

      // Navigate to engagement detail page
      console.log('Navigating to engagement:', engagement.id);
      navigate({ to: "/engagement/$engagementId", params: { engagementId: engagement.id } });
    } catch (err: any) {
      console.error("Error opening engagement:", err);
      setError(err.message || "Failed to load engagement");
      setIsLoading(false);
    }
  };

  const handleEngagementCreated = () => {
    loadEngagements();
    refreshEngagements();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getEngagementTypeBadge = (type: string) => {
    switch (type) {
      case "Financial statement audit":
        return { label: "Audit", variant: "default" as const, className: "bg-blue-500/20 text-blue-300 border-blue-500/30" };
      case "Limited Review":
        return { label: "Review", variant: "default" as const, className: "bg-purple-500/20 text-purple-300 border-purple-500/30" };
      case "Other Engagement":
        return { label: "Other", variant: "default" as const, className: "bg-green-500/20 text-green-300 border-green-500/30" };
      default:
        return { label: type, variant: "default" as const, className: "bg-gray-500/20 text-gray-300 border-gray-500/30" };
    }
  };

  const getFrameworkBadge = (framework?: string) => {
    if (!framework) return null;
    if (framework.includes("Ind AS")) {
      return { label: "Ind AS", className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" };
    }
    if (framework.includes("Transition")) {
      return { label: "Transition", className: "bg-amber-500/20 text-amber-300 border-amber-500/30" };
    }
    return { label: "AS", className: "bg-blue-500/20 text-blue-300 border-blue-500/30" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Verity AI Logo Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={logoPath}
            alt="Verity AI Logo"
            className="w-[600px] h-[600px] opacity-[0.03] object-contain"
            key="watermark-logo"
          />
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header Section */}
        <div className="border-b border-white/10 bg-black/40 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <img
                  src={logoPath}
                  alt="Verity AI"
                  className="h-8 w-8 object-contain"
                  key="header-logo"
                />
                <div>
                  <h1 className="text-3xl font-bold text-white">Engagements</h1>
                  <p className="text-sm text-muted-foreground mt-1">Manage and access your audit engagements</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="mb-1">
                    <img
                      src={logoNameDark}
                      alt="Verity AI"
                      className="h-12 object-contain"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Audit Engagement Platform</p>
                </div>
                <div className="h-6 w-px bg-white/20"></div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await logout();
                    window.location.reload();
                  }}
                  className="flex items-center gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  title="Log Out"
                  aria-label="Log out"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Log Out</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Loading engagements...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={loadEngagements} variant="outline" className="border-white/10 text-white">
                Retry
              </Button>
            </div>
          ) : apiEngagements.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
              <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20">
                <Building2 className="h-10 w-10 text-blue-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">No Engagements Yet</h3>
              <p className="text-muted-foreground mb-8 text-center max-w-md">
                Get started by creating your first engagement. You can add multiple clients and manage all your audit work from here.
              </p>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                size="lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Engagement
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Total Engagements</p>
                        <p className="text-2xl font-bold text-white">{apiEngagements.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <CheckCircle2 className="h-6 w-6 text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Active</p>
                        <p className="text-2xl font-bold text-white">{apiEngagements.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">This Year</p>
                        <p className="text-2xl font-bold text-white">
                          {apiEngagements.filter(e => e.status === 'Active').length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Engagements Grid */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white">All Engagements</h2>
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Engagement
                  </Button>
                </div>

                <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {apiEngagements.map((engagement) => {
                    const client = clients.get(engagement.audit_client_id);
                    const clientName = client?.client_name || 'Unknown Client';
                    const engagementName = engagement.engagement_name || `Engagement ${engagement.id.substring(0, 8)}`;

                    return (
                      <Card
                        key={engagement.id}
                        className="group cursor-pointer border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-300 hover:border-white/20 hover:bg-white/10"
                        onClick={() => handleOpenEngagement(engagement)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-white mb-2 group-hover:text-blue-400 transition-colors truncate">
                                {engagementName}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground truncate mb-2">
                                {clientName}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={engagement.status === 'Active' ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-gray-500/20 text-gray-300 border-gray-500/30'}>
                                  {engagement.status}
                                </Badge>
                              </div>
                            </div>
                            <ArrowRight className="h-5 w-5 text-white/40 group-hover:text-blue-400 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Building2 className="h-4 w-4 flex-shrink-0" />
                              <span className="truncate">{clientName}</span>
                            </div>
                            {engagement.teamMembers && engagement.teamMembers.length > 0 && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>{engagement.teamMembers.length} team member{engagement.teamMembers.length !== 1 ? 's' : ''}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 mt-auto py-6">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Verity AI. All rights reserved.
            </p>
          </div>
        </div>



        {/* Engagement Creation Modal */}
        <EngagementCreationModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={(engagement) => {
            handleEngagementCreated();
            setIsCreateModalOpen(false);
          }}
        />
      </div>
    </div>
  );
}
